import { optionsFunction } from "./_config";
import axios from "axios";

/**
 * Offer Trigger Constants
 * These define the available trigger types for fetching offers from the API
 */
export const OFFER_TRIGGERS = {
  LOBBY: "lobby",
  // Additional triggers will be added here as the system evolves
  // LEVELUP: "levelup",
  // BAILOUT: "bailout",
  // DAILY: "daily",
  // etc.
};

/**
 * Fetches a single offer from the v2 API based on a specific trigger type
 *
 * @param {string} trigger - The trigger type (use OFFER_TRIGGERS constants)
 * @returns {Promise<Object>} Promise resolving to the offer data (returns 1 or 0 offer)
 *
 * @example
 * fetchOfferByTrigger(OFFER_TRIGGERS.LOBBY)
 *   .then(data => console.log(data.offer))
 *   .catch(error => console.error(error));
 */
export const fetchOfferByTrigger = async (trigger) => {
  if (!trigger) {
    return Promise.reject(new Error("Trigger parameter is required"));
  }

  return await axios
    .get(`/api/v2/payment/offer/${trigger}`, optionsFunction())
    .then((res) => res.data.data)
    .catch((error) => {
      console.error(`Error fetching offer for trigger: ${trigger}`, error);
      throw error;
    });
};

/**
 * Creates an Adyen payment session for a v2 offer package
 *
 * @param {Object} payload - Payment session payload
 * @param {string} payload.locale - Locale (e.g., 'de_DE')
 * @param {number} payload.packageId - The package ID from the offer
 * @param {number} [payload.offerId] - The offer ID (nullable)
 * @param {string} payload.returnUrl - Return URL after payment
 * @returns {Promise<Object>} Promise resolving to session data
 *
 * @example
 * createAdyenSessionV2({
 *   locale: 'de_DE',
 *   packageId: 123,
 *   offerId: 456,
 *   returnUrl: 'https://...'
 * })
 */
export const createAdyenSessionV2 = (payload) =>
  axios
    .post(
      `/api/v2/payment/adyen/session`,
      {
        locale: payload.locale,
        package_id: payload.packageId,
        offer_id: payload.offerId,
        return_url: payload.returnUrl,
      },
      optionsFunction()
    )
    .then((res) => res.data.data)
    .catch((error) => {
      console.error("Error creating Adyen session (v2):", error);
      throw error;
    });

/**
 * Creates a PayPal order for a v2 offer package
 *
 * @param {number} packageId - The package ID from the offer
 * @param {number} [offerId] - The offer ID (nullable)
 * @returns {Promise<Object>} Promise resolving to PayPal order data
 *
 * @example
 * createPaypalOrderV2(123, 456)
 *   .then(res => console.log(res.data))
 *   .catch(error => console.error(error));
 */
export const createPaypalOrderV2 = (packageId, offerId) =>
  axios
    .post(
      "/api/v2/payment/paypal/create",
      {
        package_id: packageId,
        offer_id: offerId,
      },
      optionsFunction()
    )
    .then((res) => res)
    .catch((error) => {
      console.error("Error creating PayPal order (v2):", error);
      throw error;
    });

/**
 * Captures a PayPal payment for a v2 offer package
 *
 * @param {string} transactionId - The transaction reference ID
 * @param {string} orderId - The PayPal order ID
 * @returns {Promise<Object>} Promise resolving to capture response
 *
 * @example
 * capturePaypalPaymentV2('trans_123', 'order_456')
 *   .then(res => console.log(res.data))
 *   .catch(error => console.error(error));
 */
export const capturePaypalPaymentV2 = (transactionId, orderId) =>
  axios
    .post(
      "/api/v2/payment/paypal/capture",
      {
        transaction_reference: transactionId,
        order_id: orderId,
      },
      optionsFunction()
    )
    .then((res) => res)
    .catch((error) => {
      console.error("Error capturing PayPal payment (v2):", error);
      throw error;
    });
